import React, { useMemo } from 'react';
import { Clock, AlertTriangle, ArrowRightCircle, Bed, Activity, Thermometer } from 'lucide-react';

export default function SmartReservationCard({ reservation, beds, onConfirm, onCancel, onAdmit }) {

    // 1. Calcular Contexto do Setor de Destino
    const sectorContext = useMemo(() => {
        if (!reservation.sector_id || !beds.length) return null;

        const sectorBeds = beds.filter(b => b.sectorId == reservation.sector_id); // [Fix] Model returns camelCase
        const total = sectorBeds.length;
        if (total === 0) return { occupancy: 0, prep: 0, free: 0 };

        const occupied = sectorBeds.filter(b => [2, 3, 4, 5, 6, 7, 10].includes(b.statusId)).length;
        const prep = sectorBeds.filter(b => b.statusId === 8).length; // Aguardando Higienização
        const free = sectorBeds.filter(b => b.statusId === 1 || b.statusId === 9).length; // Livre ou Higienizado (Pronto)

        return {
            occupancy: Math.round((occupied / total) * 100),
            prep,
            free,
            total
        };

    }, [reservation, beds]);

    // 2. Lógica do "Próximo Passo Provável" (Inteligência)
    const nextStep = useMemo(() => {
        // Se já está CONFIRMADA
        if (reservation.status === 'confirmed') {
            // Se tem leito específico definido, vamos ver o status dele
            if (reservation.specific_bed_id) {
                const specificBed = beds.find(b => b.id == reservation.specific_bed_id);
                if (specificBed && (specificBed.statusId === 1 || specificBed.statusId === 9)) {
                    return { text: 'Check-in Liberado', icon: '🎟️', color: 'text-green-600 font-bold' };
                }
                return { text: `Aguardando Leito ${specificBed?.number || ''}`, icon: '⏳', color: 'text-orange-500' };
            }
            return { text: 'Aguardando Liberação', icon: '⏳', color: 'text-blue-600' };
        }

        if (!sectorContext) return { text: 'Análise de Vaga', icon: '🔍', color: 'text-gray-500' };

        // Prioridade 1: Tem vaga pronta (Livre ou Higienizada)
        if (sectorContext.free > 0) return { text: 'Alocação Imediata', icon: '✅', color: 'text-green-600 font-bold' };

        // Prioridade 2: Não tem vaga, mas tem leito sujo/higienizando
        if (sectorContext.prep > 0) return { text: 'Aguardar Higienização', icon: '🧼', color: 'text-orange-500' };

        // Prioridade 3: Lotado (Sem vaga e sem previsão imediata)
        return { text: 'Avaliar Alta / Transferência', icon: '⚠️', color: 'text-red-500' };
    }, [sectorContext, reservation]);

    // 3. Risco e SLA
    const getRiskInfo = () => {
        const created = new Date(reservation.created_at);
        const now = new Date();
        const diffMins = Math.floor((now - created) / 60000);
        const hours = Math.floor(diffMins / 60);
        const mins = diffMins % 60;

        const timeString = `${hours}h ${mins}m`;

        let risk = 'BAIXO';
        let colorClass = 'border-l-4 border-green-500';

        if (reservation.priority === 'Alta') {
            if (hours >= 1) { risk = 'ALTO'; colorClass = 'border-l-4 border-red-500 bg-red-50/30'; }
            else { risk = 'MÉDIO'; colorClass = 'border-l-4 border-yellow-500'; }
        } else if (hours >= 4) {
            risk = 'MÉDIO'; colorClass = 'border-l-4 border-yellow-500';
        }

        return { timeString, risk, colorClass };
    };

    const { timeString, risk, colorClass } = getRiskInfo();

    const isScheduledFuture = useMemo(() => {
        if (!reservation.scheduled_date) return false;
        const today = new Date().toISOString().split('T')[0];
        return reservation.scheduled_date > today;
    }, [reservation.scheduled_date]);

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col ${colorClass}`}>
            {/* Header: ID, Tempo, SLA */}
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 flex justify-between items-center border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-gray-500">#{reservation.id}</span>
                    {isScheduledFuture && (
                        <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded border border-purple-200">
                            📅 {new Date(reservation.scheduled_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {risk === 'ALTO' && <AlertTriangle size={16} className="text-red-500" />}
                </div>
                <div className="flex items-center gap-3 text-sm font-medium">
                    <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                        <Clock size={14} /> {timeString}
                    </div>
                    <div className={`px-2 py-0.5 rounded text-xs uppercase font-bold ${risk === 'ALTO' ? 'bg-red-100 text-red-600' :
                        risk === 'MÉDIO' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                        }`}>
                        Risco: {risk}
                    </div>
                </div>
            </div>

            {/* Corpo Principal */}
            <div className="p-4 flex-1 space-y-4">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-mono text-gray-400">
                                #{reservation.id}
                            </span>
                            {/* Se tiver Nome, mostra Nome com destaque. Senão, mostra a Origem */}
                            {reservation.patient_name ? (
                                <h4 className="font-bold text-lg text-gray-800 dark:text-gray-100 m-0">
                                    {reservation.patient_name}
                                </h4>
                            ) : (
                                <h4 className="font-bold text-lg text-gray-800 dark:text-gray-100 m-0">
                                    {reservation.origin}
                                </h4>
                            )}
                        </div>

                        {/* Subtítulo muda: se mostrou nome em cima, mostra origem aqui */}
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                            {reservation.patient_name && (
                                <span className="badge bg-light text-dark border mr-1">
                                    {reservation.origin}
                                </span>
                            )}
                            <span>{reservation.patient_gender}</span>
                            <span>•</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${reservation.patient_condition === 'Crítica' ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                {reservation.patient_condition}
                            </span>
                            {reservation.patient_precautions && reservation.patient_precautions !== 'Nenhuma' && (
                                <span className="text-orange-500 font-bold flex items-center gap-1">
                                    • <AlertTriangle size={12} /> {reservation.patient_precautions}
                                </span>
                            )}
                            {/* [Novo] Badge de Transferência no Header se aplicável */}
                            {reservation.observation && reservation.observation.includes('TRANSFERÊNCIA') && (
                                <span className="ms-auto bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs font-bold border border-indigo-200">
                                    TRANSFERÊNCIA
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <hr className="border-gray-100 dark:border-gray-700" />

                {/* Linha 2: Contexto do Destino */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-bold mb-1">Destino</p>
                        <p className="font-bold text-sm text-gray-900 dark:text-white leading-tight">
                            {reservation.sector?.name || 'Indefinido'}
                            {reservation.specific_bed_id && (() => {
                                const bed = beds.find(b => b.id == reservation.specific_bed_id);
                                return bed ? ` - Leito ${bed.number}` : '';
                            })()}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{reservation.bed_type}</p>
                    </div>

                    {/* Mini Dashboard do Setor */}
                    {sectorContext && (
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-2 rounded-lg text-xs space-y-1">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Ocupação:</span>
                                <span className={`font-bold ${sectorContext.occupancy > 90 ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                    {sectorContext.occupancy}%
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Em Preparo:</span>
                                <span className="font-bold text-orange-500">{sectorContext.prep}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Livres:</span>
                                <span className="font-bold text-green-600">{sectorContext.free}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Linha 3: Próximo Passo */}
                <div className="flex items-center gap-3 p-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-lg">
                    <span className="text-lg">{nextStep.icon}</span>
                    <div>
                        <p className="text-xs text-gray-500 font-bold uppercase">Próximo Passo Sugerido</p>
                        <p className={`text-sm font-bold ${nextStep.color}`}>{nextStep.text}</p>
                    </div>
                </div>
            </div>

            {/* Footer: Ações */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
                {/* [FIX] Priorizar detecção de Transferência (Tipo, Observação ou Contexto) */}
                {(reservation.type === 'transfer' || (reservation.observation && reservation.observation.includes('TRANSFERÊNCIA')) || (reservation.origin && reservation.origin.toLowerCase().includes('transferência'))) ? (
                    <div className="w-full flex flex-col gap-2">
                        <span className="w-full bg-yellow-100 text-yellow-800 px-4 py-3 rounded-lg text-sm font-bold flex items-center justify-center border border-yellow-200 animate-pulse">
                            <i className="bi bi-truck me-2"></i>
                            EM TRANSFERÊNCIA
                            {reservation.specific_bed_id && beds.find(b => b.id === reservation.specific_bed_id) &&
                                ` ➔ Leito ${beds.find(b => b.id === reservation.specific_bed_id)?.number}`
                            }
                        </span>

                        <button
                            onClick={onCancel}
                            className="w-full px-3 py-2 text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors uppercase border border-transparent hover:border-red-100 rounded text-center"
                            title="Cancelar Transferência"
                        >
                            Cancelar Transferência
                        </button>
                    </div>
                ) : (
                    <>
                        {reservation.status === 'pending' ? (
                            <>
                                <button
                                    onClick={onCancel}
                                    className="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-red-600 transition-colors uppercase"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={onConfirm}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow hover:shadow-md transition-all flex items-center justify-center gap-2"
                                >
                                    Decidir <ArrowRightCircle size={16} />
                                </button>
                            </>
                        ) : reservation.status === 'confirmed' ? (
                            <div className="flex justify-end w-full gap-2">
                                <button
                                    onClick={onCancel}
                                    className="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-red-600 transition-colors uppercase border border-transparent hover:border-gray-200 rounded"
                                    title="Cancelar Reserva"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClickCapture={(e) => {
                                        e.preventDefault();
                                        if (onAdmit) onAdmit(reservation);
                                    }}
                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow hover:shadow-md transition-all flex items-center justify-center gap-2"
                                >
                                    <Activity size={16} /> Realizar Internação
                                </button>
                            </div>
                        ) : reservation.status === 'waiting_admission' ? (
                            <div className="w-full flex justify-center">
                                <span className="w-full bg-orange-100 text-orange-800 px-4 py-2 rounded-lg text-sm font-bold flex items-center justify-center border border-orange-200">
                                    <Clock size={16} className="mr-2" /> Aguardando Check-in
                                </span>
                            </div>
                        ) : reservation.status === 'admission_in_progress' ? (
                            <div className="w-full flex justify-center flex-col gap-2">
                                <span className="w-full bg-blue-100 text-blue-800 px-4 py-2 rounded-lg text-sm font-bold flex items-center justify-center border border-blue-200 animate-pulse">
                                    <Activity size={16} className="mr-2" /> Em Processo de Admissão
                                </span>
                                <div className="flex justify-center text-xs text-gray-400 gap-2">
                                    <span>Kit 🛏️</span> ➔ <span>Enfermagem 💉</span> ➔ <span>Acomodação 🏠</span>
                                </div>
                            </div>
                        ) : reservation.status === 'admitted' ? (
                            <div className="w-full flex flex-col gap-2">
                                <span className="w-full bg-green-100 text-green-800 px-4 py-2 rounded-lg text-sm font-bold flex items-center justify-center border border-green-200">
                                    <Activity size={16} className="mr-2" /> Paciente Internado
                                </span>
                                <button
                                    onClick={(e) => {
                                        if (onArchive) onArchive();
                                    }}
                                    className="w-full px-3 py-2 text-xs font-semibold text-gray-500 hover:text-green-600 transition-colors uppercase border border-gray-200 rounded hover:bg-gray-50"
                                >
                                    📂 Arquivar Card
                                </button>
                            </div>
                        ) : (
                            <span className="text-sm font-medium text-gray-400 w-full text-center py-2">
                                Encerrada
                            </span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
